import React, { memo, CSSProperties, useEffect, useCallback, useState, useRef } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from '@xyflow/react';
import { EditableText, Icon, Button, ContextMenu, Menu, MenuItem, MenuDivider, Tooltip } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { observer } from 'mobx-react-lite';
import MindmapNodeToolbar from '../NodeToolbar';
import LeadSelector from '../../Lead/LeadSelector';
import StatusSelector from '../../Status/StatusSelector';
import styles from './NodeTypes.module.css';
import api from '../../../services/api';
import { useAppStore } from '../../../stores/UnifiedStoreProvider';
import { NodeStatus, getStatusConfig } from '../../../constants/nodeStatus';

// Node style function - neutral styling with status-based left border
const getNodeStyle = (
  selected: boolean, 
  hasIndicators: boolean = false,
  isHovered: boolean = false,
  priority: string = 'medium',
  status?: NodeStatus | null
): CSSProperties => {
  // Check if dark mode is active
  const isDark = document.body.classList.contains('bp5-dark');
  
  const statusConfig = status ? getStatusConfig(status) : null;
  const borderColor = selected ? '#4A90E2' : (isDark ? '#5f6b7c' : '#d1d5db');
  
  // Dark mode colors (Blueprint.js official)
  const bgLight = selected ? '#f0f7ff' : (statusConfig ? statusConfig.backgroundColor : '#ffffff');
  const bgDark = selected ? 'rgba(138, 187, 255, 0.15)' : '#2f343c'; // dark-gray3
  const textLight = selected ? '#2E5C8A' : '#374151';
  const textDark = '#f6f7f9'; // light-gray5
  
  return {
    // Uniform border with status-based left border
    borderStyle: 'solid',
    borderWidth: '2px',
    borderColor: borderColor,
    borderLeftWidth: statusConfig ? '4px' : '2px',
    borderLeftColor: statusConfig ? statusConfig.borderColor : borderColor,
    borderRadius: '8px',
    backgroundColor: isDark ? bgDark : bgLight,
    boxShadow: selected 
      ? (isDark ? '0 4px 12px rgba(138, 187, 255, 0.3)' : '0 4px 12px rgba(74, 144, 226, 0.3)') 
      : (isDark ? '0 2px 8px rgba(0, 0, 0, 0.4)' : '0 2px 8px rgba(0, 0, 0, 0.08)'),
    padding: '12px 45px 12px 20px',
    paddingBottom: '12px',
    minWidth: isHovered ? '180px' : '140px',
    maxWidth: '400px',
    minHeight: '45px',
    fontWeight: selected ? 600 : 500,
    color: isDark ? textDark : textLight,
    cursor: 'pointer',
    fontSize: '14px',
    position: 'relative',
    transition: 'all 0.2s ease',
  };
};

// Handle style - adapts to theme
const getHandleStyle = (): CSSProperties => {
  const isDark = document.body.classList.contains('bp5-dark');
  return {
    width: '8px',
    height: '8px',
    backgroundColor: '#4A90E2',
    border: isDark ? '2px solid #2f343c' : '2px solid #ffffff',
  };
};

// Add button style
const addButtonStyle: CSSProperties = {
  position: 'absolute',
  right: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  width: '24px',
  height: '24px',
  borderRadius: '4px',
  backgroundColor: 'transparent',
  color: '#4A90E2',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: '16px',
  fontWeight: 'bold',
  cursor: 'pointer',
  border: '1px solid #4A90E2',
  opacity: 0,
  pointerEvents: 'none',
  transition: 'all 0.2s ease',
};

const addButtonVisibleStyle: CSSProperties = {
  ...addButtonStyle,
  opacity: 1,
  pointerEvents: 'auto',
};


// Indicator badge style (inline with avatar)
const baseIndicator: CSSProperties = {
  borderRadius: '6px',
  padding: '2px 5px',
  fontSize: '10px',
  fontWeight: '600',
  display: 'flex',
  alignItems: 'center',
  gap: '3px',
  minWidth: '18px',
  height: '18px',
  justifyContent: 'center',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
  color: 'white',
};

interface WorkflowNodeData {
  label: string;
  priority?: 'low' | 'medium' | 'high';
  isNecessary?: boolean;
  status?: NodeStatus | null;
  lead?: string;
  notes?: string;
  commentCount?: number;
  repositoryCount?: number;
  linkCount?: number;
  hasDescription?: boolean;
  documentCount?: number;
  onDelete?: (id: string, deleteChildren?: boolean) => void;
  onAdd?: (parentId: string) => void;
  onUpdate?: (id: string, data: any) => void;
  onExpand?: (nodeId: string) => void;
}

const EnhancedWorkflowNode: React.FC<NodeProps<WorkflowNodeData>> = observer(({ 
  id, 
  data, 
  selected 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [nodeLabel, setNodeLabel] = useState(data.label || '');
  
  const { getNode, setNodes, addNodes, setEdges, getEdges, setCenter } = useReactFlow();
  const appStore = useAppStore();
  const { mindmap } = appStore;
  
  // Get the node from the store to read reactive values
  const storeNode = mindmap.nodes.find(n => n.id === id);
  
  // Read reactive values from the store (not local state!)
  const priority = storeNode?.priority || data.priority || 'medium';
  const isNecessary = storeNode?.isNecessary ?? data.isNecessary ?? false;
  const nodeStatus = storeNode?.status || data.status || null;
  const nodeLead = storeNode?.lead || data.lead || null;
  
  // Auto-focus and enter edit mode for new nodes with empty labels or default "New Node" label
  useEffect(() => {
    const isNewNode = !data.label || data.label === 'New Node';
    if (selected && isNewNode && !isEditing) {
      console.log(`🎯 [EnhancedWorkflowNode] Auto-focusing new node: ${id}`);
      setIsEditing(true);
    }
  }, [selected, data.label, id, isEditing]);
  
  // Get indicator counts from node data
  const commentCount = data.commentCount || 0;
  const repositoryCount = data.repositoryCount || 0;
  const linkCount = data.linkCount || 0;
  const hasDescription = data.hasDescription || false;
  const documentCount = data.documentCount || 0;
  
  const handleLabelChange = useCallback((newLabel: string) => {
    setNodeLabel(newLabel);
  }, []);
  
  const handleLabelConfirm = useCallback(async (confirmedLabel: string) => {
    const finalLabel = confirmedLabel.trim() || 'New Node';
    setNodeLabel(finalLabel);
    
    // Update node in React Flow
    setNodes((nodes) => 
      nodes.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: { ...node.data, label: finalLabel }
          };
        }
        return node;
      })
    );
    
    // Call update callback if provided
    if (data.onUpdate) {
      data.onUpdate(id, { label: finalLabel });
    }
    
    // TODO: Update in backend
    try {
      // Auth is handled via httpOnly cookies
      await api.patch(
        `/mindmaps/nodes/${id}`,
        { label: finalLabel },
        
      );
    } catch (error) {
      console.error('Error updating node label:', error);
    }
  }, [id, setNodes, data]);

  const handleAddNode = useCallback(async (event: React.MouseEvent) => {
    event.stopPropagation();
    
    const node = getNode(id);
    if (!node) return;
    
    // Use MobX store for optimistic creation
    
    try {
      // Create node optimistically through store
      const result = await mindmap.createNodeOptimistically(id);
      
      // Select the new node so auto-focus triggers
      if (result && result.tempId) {
        setTimeout(() => {
          setNodes((nodes) => nodes.map((n) => ({
            ...n,
            selected: n.id === result.tempId
          })));
        }, 50);
      }
      
      // Call add callback if provided
      if (data.onAdd) {
        data.onAdd(id);
      }
    } catch (error) {
      console.error('Error creating node:', error);
    }
  }, [id, getNode, mindmap, setNodes, data]);

  const handlePriorityChange = useCallback((newPriority: 'low' | 'medium' | 'high') => {
    // Update the store (which will trigger reactivity in both sidebar and node)
    mindmap.updateNodeOptimistically(id, { priority: newPriority });
    
    // Also update ReactFlow's internal state for immediate visual feedback
    setNodes((nodes) => 
      nodes.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: { ...node.data, priority: newPriority }
          };
        }
        return node;
      })
    );
  }, [id, setNodes, mindmap]);

  const handleNecessaryChange = useCallback((newNecessary: boolean) => {
    // Update the store (which will trigger reactivity in both sidebar and node)
    mindmap.updateNodeOptimistically(id, { isNecessary: newNecessary });
    
    // Also update ReactFlow's internal state for immediate visual feedback
    setNodes((nodes) => 
      nodes.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: { ...node.data, isNecessary: newNecessary }
          };
        }
        return node;
      })
    );
  }, [id, setNodes, mindmap]);

  const handleStatusChange = useCallback((newStatus: NodeStatus | null) => {
    // Update the store (which will trigger reactivity in both sidebar and node)
    mindmap.updateNodeOptimistically(id, { status: newStatus });
    
    // Also update ReactFlow's internal state for immediate visual feedback
    setNodes((nodes) => 
      nodes.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: { ...node.data, status: newStatus }
          };
        }
        return node;
      })
    );
  }, [id, setNodes, mindmap]);

  const handleDelete = useCallback(async (deleteChildren: boolean = false) => {
    console.log(`🗑️ [EnhancedWorkflowNode] Deleting node ${id}, deleteChildren: ${deleteChildren}`);
    
    try {
      // Call MobX store to handle optimistic deletion + backend API call
      await mindmap.deleteNodeOptimistically(id, deleteChildren);
      
      console.log(`✅ [EnhancedWorkflowNode] Node deleted successfully`);
    } catch (error) {
      console.error(`❌ [EnhancedWorkflowNode] Delete failed:`, error);
    }
  }, [id, mindmap]);

  return (
    <>
      {selected && (
        <MindmapNodeToolbar
          nodeId={id}
          initialPriority={priority}
          initialNecessary={isNecessary}
          onPriorityChange={handlePriorityChange}
          onNecessaryChange={handleNecessaryChange}
          onDeleteNode={handleDelete}
        />
      )}
      <Tooltip
        content="CMD + Click to open sidebar"
        position="bottom"
        hoverOpenDelay={500}
        disabled={isEditing}
      >
        <div
          style={getNodeStyle(!!selected, 
            commentCount > 0 || repositoryCount > 0 || linkCount > 0 || hasDescription,
            isHovered, 
            priority,
            nodeStatus)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        onContextMenu={(e) => {
          e.preventDefault();
          const menu = (
            <Menu>
              <MenuItem icon={IconNames.EDIT} text="Edit Label" onClick={() => setIsEditing(true)} />
              <MenuItem icon={IconNames.DUPLICATE} text="Duplicate" onClick={async () => {
                const node = getNode(id);
                if (node) {
                  await mindmap.createNodeOptimistically(
                    node.data.parentId
                  );
                }
              }} />
              <MenuDivider />
              <MenuItem icon={IconNames.ADD} text="Add Child" onClick={(e: any) => handleAddNode(e)} />
              <MenuDivider />
              <MenuItem icon={IconNames.TRASH} text="Delete" intent="danger" onClick={handleDelete} />
            </Menu>
          );
          ContextMenu.show(menu, { left: e.clientX, top: e.clientY });
        }}
        className={`${styles.workflowNode} mindmap-node priority-${priority} ${selected ? 'selected' : ''}`}
      >
        <Handle 
          type="target" 
          position={Position.Left} 
          style={getHandleStyle()}
        />
        
        <EditableText
          value={nodeLabel}
          onChange={handleLabelChange}
          onConfirm={handleLabelConfirm}
          placeholder="Enter node label"
          multiline={false}
          selectAllOnFocus={true}
          className={styles.nodeLabel}
          style={{
            textAlign: 'center',
            width: '100%',
            maxWidth: '320px',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        />

        {/* Status, Lead Avatar and metadata badges row (below title) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
            marginTop: '6px',
            gap: '6px',
          }}
        >
          {/* Status Badge */}
          <StatusSelector
            currentStatus={nodeStatus}
            onStatusSelect={handleStatusChange}
            minimal={true}
            showLabel={false}
          />
          
          {/* Lead Avatar */}
          <LeadSelector
            currentLeadId={nodeLead}
            onLeadSelect={(leadId) => {
              // Update the store (which will trigger reactivity in both sidebar and node)
              mindmap.updateNodeOptimistically(id, { lead: leadId });
            }}
            minimal={true}
            showLabel={false}
          />
          
          {/* Metadata badges inline with avatar */}
          {repositoryCount > 0 && (
            <div style={{ ...baseIndicator, backgroundColor: '#24292f' }} title={`${repositoryCount} repository${repositoryCount > 1 ? 'ies' : ''}`}>
              <Icon icon="git-branch" size={9} color="white" />
              <span>{repositoryCount}</span>
            </div>
          )}
          {linkCount > 0 && (
            <div style={{ ...baseIndicator, backgroundColor: '#0969da' }} title={`${linkCount} link${linkCount > 1 ? 's' : ''}`}>
              <Icon icon={IconNames.LINK} size={9} color="white" />
              <span>{linkCount}</span>
            </div>
          )}
          {commentCount > 0 && (
            <div style={{ ...baseIndicator, backgroundColor: '#8250df' }} title={`${commentCount} comment${commentCount > 1 ? 's' : ''}`}>
              <Icon icon={IconNames.COMMENT} size={9} color="white" />
              <span>{commentCount}</span>
            </div>
          )}
          {documentCount > 0 && (
            <div style={{ ...baseIndicator, backgroundColor: '#0969da' }} title={`${documentCount} document${documentCount > 1 ? 's' : ''}`}>
              <Icon icon={IconNames.DOCUMENT} size={9} color="white" />
              <span>{documentCount}</span>
            </div>
          )}
          {hasDescription && (
            <div style={{ ...baseIndicator, backgroundColor: '#bf8700', minWidth: '18px' }} title="Has notes">
              <Icon icon={IconNames.EDIT} size={9} color="white" />
            </div>
          )}
        </div>
        
        {/* Add button */}
        <button
          style={isHovered ? addButtonVisibleStyle : addButtonStyle}
          onClick={handleAddNode}
          className={styles.addButton}
          title="Add child node"
        >
          +
        </button>
        
        
        <Handle 
          type="source" 
          position={Position.Right} 
          style={getHandleStyle()}
        />
      </div>
      </Tooltip>
    </>
  );
});

EnhancedWorkflowNode.displayName = 'EnhancedWorkflowNode';

export default EnhancedWorkflowNode;
